import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, X, Image as ImageIcon } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFAB from "@/components/WhatsAppFAB";
import { useI18n } from "@/context/I18nContext";

// Real farm footage + photos only — no AI-generated content
const GALLERY = [
  {
    type: "video",
    title: "Morning at the Farm",
    titleHi: "फार्म की सुबह",
    desc: "4 AM — our herd begins the day.",
    thumb: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/trqpbbku_WhatsApp%20Image%202026-04-21%20at%209.39.17%20AM.jpeg",
    src: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/nc0zz85h_vid1.mp4",
  },
  {
    type: "video",
    title: "Grass-fed & Loved",
    titleHi: "प्रेम से पोषित",
    desc: "Fresh fodder, hand-fed twice a day.",
    thumb: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/jp6yibsj_WhatsApp%20Image%202026-04-21%20at%201.40.32%20PM.jpeg",
    src: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/g55167ps_vid%202.mp4",
  },
  {
    type: "video",
    title: "Our Gaushala",
    titleHi: "हमारी गौशाला",
    desc: "The heart of Divya Dhara.",
    thumb: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/c16efud5_WhatsApp%20Image%202026-04-21%20at%209.51.33%20AM.jpeg",
    src: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/y2v4isyf_vid%203.mp4",
  },
  {
    type: "video",
    title: "Inside the Shed",
    titleHi: "गौशाला के अंदर",
    desc: "Hand-feeding cattle at Matrubhumi.",
    thumb: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/1tbdeqa7_WhatsApp%20Image%202026-04-21%20at%209.51.34%20AM.jpeg",
    src: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/jmwdpn85_WhatsApp%20Video%202026-04-21%20at%209.38.54%20AM.mp4",
  },
  {
    type: "photo",
    title: "Herd at Rest",
    titleHi: "विश्रामरत गायें",
    desc: "Our Desi cows in their open pasture.",
    thumb: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/z3wci6lm_WhatsApp%20Image%202026-04-21%20at%209.51.33%20AM.jpeg",
    src: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/z3wci6lm_WhatsApp%20Image%202026-04-21%20at%209.51.33%20AM.jpeg",
  },
  {
    type: "photo",
    title: "Kankrej Close-up",
    titleHi: "कांकरेज निकट से",
    desc: "Pure indigenous breed at Matrubhumi.",
    thumb: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/jp6yibsj_WhatsApp%20Image%202026-04-21%20at%201.40.32%20PM.jpeg",
    src: "https://customer-assets.emergentagent.com/job_pure-milk-navi/artifacts/jp6yibsj_WhatsApp%20Image%202026-04-21%20at%201.40.32%20PM.jpeg",
  },
];

export default function Gallery() {
  const { lang } = useI18n();
  const [active, setActive] = useState(null);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  return (
    <div className="bg-dd-page min-h-screen" data-testid="gallery-page">
      <Navbar />
      <div className="pt-28 md:pt-36 dd-container pb-20">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="dd-label text-dd-orange">{lang === "hi" ? "गैलरी" : "Gallery"}</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-ink mt-3">
            {lang === "hi" ? "हमारी गौशाला से कहानियाँ" : "Stories from our Gaushala"}
          </h1>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
          <p className="text-dd-ink-muted">
            {lang === "hi" ? "हर वीडियो हमारे फार्म की असली झलक है — अनफ़िल्टर्ड व शुद्ध।" : "Every clip is actual footage from our farm — unfiltered and honest."}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {GALLERY.map((v, idx) => (
            <motion.button
              key={idx}
              onClick={() => setActive(v)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (idx % 3) * 0.08 }}
              className="group text-left bg-white rounded-xl border border-dd-line hover:border-dd-gold hover:shadow-xl overflow-hidden transition-all"
              data-testid={`gallery-card-${idx}`}
            >
              <div className="aspect-video relative overflow-hidden">
                <img src={v.thumb} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-dd-ink/50 to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-14 h-14 rounded-full bg-dd-orange text-white flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                    {v.type === "video" ? (
                      <Play className="w-5 h-5 ml-0.5" fill="currentColor" strokeWidth={0} />
                    ) : (
                      <ImageIcon className="w-5 h-5" strokeWidth={1.5} />
                    )}
                  </span>
                </span>
                <div className="absolute top-3 left-3 bg-white/95 text-dd-orange px-2 py-0.5 rounded-full text-[0.6rem] font-bold tracking-wider">
                  {v.type === "video" ? "REAL FARM FOOTAGE" : "FROM OUR GAUSHALA"}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-serif text-xl text-dd-ink">{lang === "hi" ? v.titleHi : v.title}</h3>
                <p className="text-sm text-dd-ink-muted mt-1">{v.desc}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {active && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setActive(null)} data-testid="gallery-modal">
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setActive(null)} className="absolute -top-10 right-0 text-white text-sm flex items-center gap-1 hover:text-dd-gold">
              <X className="w-4 h-4" /> Close
            </button>
            {active.type === "video" ? (
              <video src={active.src} controls autoPlay playsInline className="w-full aspect-video rounded-lg bg-black" />
            ) : (
              <img src={active.src} alt={active.title} className="w-full max-h-[80vh] object-contain rounded-lg bg-black" />
            )}
          </div>
        </div>
      )}

      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
